import React, { useState } from "react";
import { useI18n } from "../../i18n";
import { formatAmount } from "../utils/money";
import { isComplete, slotTotal, withChoices } from "../utils/packs";
import Sheet from "./Sheet";

/**
 * A pack with a range in it ("a Pos device, a mesh coil, a salt juice") asks
 * what the customer wants for each of those parts before it goes on the ticket.
 * Fixed parts are listed so the cashier sees what the bag holds; each range
 * shows its variants with what is left, and only ones with stock can be picked.
 */
const PackChoiceSheet = ({ pack, onConfirm, onClose }) => {
  const { t } = useI18n();
  const [picks, setPicks] = useState({});

  const change = (item, option, delta) =>
    setPicks((current) => {
      const slot = { ...(current[item.id] || {}) };

      if (item.quantity === 1 && delta > 0) {
        // One to choose: tapping another variant replaces the choice
        return { ...current, [item.id]: { [option.product_id]: 1 } };
      }

      const next = (slot[option.product_id] || 0) + delta;
      if (next < 0 || next > option.stock_quantity) return current;
      if (delta > 0 && slotTotal(current, item) >= item.quantity) return current;

      if (next === 0) delete slot[option.product_id];
      else slot[option.product_id] = next;

      return { ...current, [item.id]: slot };
    });

  const complete = isComplete(pack, picks);

  return (
    <Sheet
      open
      onClose={onClose}
      title={pack.name}
      subtitle={t("pos.packChoice.subtitle")}
      footer={
        <button
          type="button"
          disabled={!complete}
          onClick={() => onConfirm(withChoices(pack, picks))}
          className="w-full h-12 rounded-2xl bg-primary-500 hover:bg-primary-400 text-white font-bold transition-colors disabled:opacity-40"
        >
          {complete
            ? `${t("pos.packChoice.add")} · ${formatAmount(pack.price)}`
            : t("pos.packChoice.pickAll")}
        </button>
      }
    >
      <ul className="p-3 space-y-4">
        {pack.components.map((item) =>
          item.variant_group_id ? (
            <li key={item.id}>
              <div className="flex items-baseline justify-between px-1 mb-2">
                <p className="text-sm font-bold text-white">{item.product_name}</p>
                <p className="text-xs text-slate-400 tabular-nums">
                  {item.quantity === 1
                    ? t("pos.packChoice.chooseOne")
                    : `${t("pos.packChoice.chooseMany", { count: item.quantity })} · ${t("pos.packChoice.picked", { picked: slotTotal(picks, item), total: item.quantity })}`}
                </p>
              </div>

              <ul className="space-y-2">
                {item.options.map((option) => {
                  const count = picks[item.id]?.[option.product_id] || 0;
                  const out = option.stock_quantity <= 0;

                  return (
                    <li
                      key={option.product_id}
                      className={`flex items-stretch gap-2 rounded-2xl border transition-colors ${
                        count > 0
                          ? "border-emerald-400 bg-emerald-500/10"
                          : "border-slate-800 bg-slate-900"
                      } ${out ? "opacity-50" : ""}`}
                    >
                      <button
                        type="button"
                        disabled={out}
                        onClick={() => change(item, option, 1)}
                        className="flex-1 min-w-0 flex items-center justify-between gap-3 p-3 text-start active:scale-[0.98] transition-transform"
                      >
                        <span className="text-[15px] font-semibold text-white truncate">
                          {option.label || option.name}
                        </span>
                        <span className="shrink-0 text-xs tabular-nums text-slate-400">
                          {out
                            ? t("pos.packChoice.out")
                            : t("pos.packChoice.left", { count: option.stock_quantity })}
                        </span>
                      </button>

                      {count > 0 && item.quantity > 1 && (
                        <div className="shrink-0 flex items-center gap-1.5 pe-2.5">
                          <button
                            type="button"
                            onClick={() => change(item, option, -1)}
                            aria-label={t("pos.packChoice.less", { name: option.label || option.name })}
                            className="w-9 h-9 rounded-full bg-slate-800 text-white text-lg leading-none hover:bg-slate-700 active:scale-90 transition"
                          >
                            −
                          </button>
                          <span className="min-w-6 text-center text-sm font-bold text-emerald-300 tabular-nums">
                            {count}
                          </span>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ) : (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-slate-800/50 text-sm"
            >
              <span className="text-slate-200 truncate">
                {item.quantity} × {item.product_name}
              </span>
              <span className="shrink-0 text-xs text-slate-400">{t("pos.packChoice.included")}</span>
            </li>
          ),
        )}
      </ul>
    </Sheet>
  );
};

export default PackChoiceSheet;
